// Overlay "Journal" (caméra zoom 1) : historique défilable des actions/événements du collège.
// Données : ServerLink.log (semé par 'server-log-history', augmenté par 'server-log-entry').
// Défilement custom (conteneur + masque géométrique) — PAS de rexUI (version épinglée 1.1.85).
import 'phaser'
import { SceneName } from './enums/SceneNames'
import GlobalInfo from '../GlobalInfo'
import ServerLink from '../net/ServerLink'
import type { LogEntry, LogKind } from '../net/protocol'

const KIND_LABEL: Record<LogKind, string> = { talk: 'parle', use: 'fait', propose: 'événement', relationship: 'lien', create: 'création', school: 'école', evolve: 'évolution' }
const KIND_COLOR: Record<LogKind, string> = { talk: '#63b3ed', use: '#68d391', propose: '#f6e05e', relationship: '#fc8181', create: '#b794f4', school: '#f6ad55', evolve: '#f687b3' }
const PAD = 12, ROW_GAP = 4, TITLE_H = 34, RENDER_MAX = 200

export default class LogScene extends Phaser.Scene {
    private open = false
    private panel!: Phaser.GameObjects.Rectangle
    private titleText!: Phaser.GameObjects.Text
    private closeHint!: Phaser.GameObjects.Text
    private list!: Phaser.GameObjects.Container
    private maskShape!: Phaser.GameObjects.Graphics
    private maskObj?: Phaser.Display.Masks.GeometryMask
    private rows: Phaser.GameObjects.Text[] = []
    private placeholder?: Phaser.GameObjects.Text
    private contentH = 0
    private scrollOffset = 0
    private vpX = 0; private vpY = 0; private vpW = 0; private vpH = 0
    private dragging = false; private dragStartY = 0; private dragStartOffset = 0

    constructor() { super(SceneName.Log) }

    create(): void {
        const W = this.scale.width, H = this.scale.height
        this.panel = this.add.rectangle(W * 0.4, 0, W * 0.6, H, 0x0c1018, 0.93)
            .setOrigin(0).setScrollFactor(0).setDepth(3000).setStrokeStyle(1, 0xffffff, 0.15).setInteractive()
        this.titleText = this.add.text(0, 8, 'Journal', { fontFamily: 'sans-serif', fontSize: '15px', fontStyle: 'bold', color: '#f7fafc' })
            .setScrollFactor(0).setDepth(3001).setResolution(3)
        this.closeHint = this.add.text(W - PAD, 11, '✕ Échap', { fontFamily: 'sans-serif', fontSize: '10px', color: '#a0aec0' })
            .setOrigin(1, 0).setScrollFactor(0).setDepth(3001).setResolution(3)
        this.list = this.add.container(0, 0).setScrollFactor(0).setDepth(3001)
        this.maskShape = this.add.graphics().setScrollFactor(0).setVisible(false)

        this.relayout()
        this.setOpen(false)

        GlobalInfo.on('toggle-journal', this.toggle, this)
        GlobalInfo.on('close-journal', this.close, this)
        GlobalInfo.on('server-log-entry', this.onLogEntry, this)
        this.scale.on('resize', this.relayout, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            GlobalInfo.off('toggle-journal', this.toggle, this)
            GlobalInfo.off('close-journal', this.close, this)
            GlobalInfo.off('server-log-entry', this.onLogEntry, this)
            this.scale.off('resize', this.relayout, this)
        })

        this.input.on('wheel', (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
            if (this.open) this.setScroll(this.scrollOffset + dy * 0.5)
        })
        this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
            if (this.open && this.inViewport(p)) { this.dragging = true; this.dragStartY = p.y; this.dragStartOffset = this.scrollOffset }
        })
        this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
            if (this.dragging) this.setScroll(this.dragStartOffset - (p.y - this.dragStartY))
        })
        this.input.on('pointerup', () => { this.dragging = false })
    }

    private relayout = (): void => {
        const W = this.scale.width, H = this.scale.height
        const px = W * 0.4
        this.panel.setPosition(px, 0).setSize(W * 0.6, H)
        this.titleText.setX(px + PAD)
        this.closeHint.setX(W - PAD)
        this.vpX = px + PAD; this.vpY = TITLE_H; this.vpW = W - px - PAD * 2; this.vpH = H - TITLE_H - PAD
        this.maskShape.clear().fillStyle(0xffffff).fillRect(this.vpX, this.vpY, this.vpW, this.vpH)
        if (!this.maskObj) { this.maskObj = this.maskShape.createGeometryMask(); this.list.setMask(this.maskObj) }
        this.applyScroll()
    }

    private toggle = (): void => this.setOpen(!this.open)
    private close = (): void => { if (this.open) this.setOpen(false) }

    private setOpen(v: boolean): void {
        this.open = v
        ;(GlobalInfo as any)._gameProgress.journalOpen = v
        this.panel.setVisible(v); this.titleText.setVisible(v); this.closeHint.setVisible(v); this.list.setVisible(v)
        if (v) this.renderFromCache()
        else this.clearRows()
    }

    private renderFromCache(): void {
        this.clearRows()
        const day = ServerLink.clock?.dayName
        this.titleText.setText(day ? `Journal — ${day.charAt(0).toUpperCase() + day.slice(1)}` : 'Journal')
        const entries = ServerLink.log.slice(-RENDER_MAX)
        if (entries.length === 0) {
            // Coords LOCALES au conteneur `list` (positionné en (vpX, vpY) par applyScroll) — pas absolues.
            this.placeholder = this.add.text(0, 4, 'Rien encore aujourd’hui…', { fontFamily: 'sans-serif', fontSize: '11px', color: '#718096' })
                .setScrollFactor(0).setDepth(3001)
            this.list.add(this.placeholder)
            return
        }
        for (const e of entries) this.addRow(e)
        this.scrollOffset = this.maxScroll()   // start pinned to the latest
        this.applyScroll()
    }

    private addRow(e: LogEntry): void {
        const label = KIND_LABEL[e.kind] ?? e.kind
        const t = this.add.text(0, this.contentH, `${e.timeLabel} · [${label}] ${e.text}`, {
            fontFamily: 'sans-serif', fontSize: '11px', color: KIND_COLOR[e.kind] ?? '#e2e8f0',
            wordWrap: { width: this.vpW },
        }).setScrollFactor(0).setDepth(3001).setLineSpacing(2)
        this.list.add(t)
        this.rows.push(t)
        this.contentH += t.height + ROW_GAP
    }

    private reflowRows(): void {
        let y = 0
        for (const r of this.rows) { r.setY(y); y += r.height + ROW_GAP }
        this.contentH = y
    }

    private onLogEntry = (p: { entry: LogEntry }): void => {
        if (!this.open) return
        if (this.placeholder) { this.placeholder.destroy(); this.placeholder = undefined }
        const pinned = this.scrollOffset >= this.maxScroll() - 4
        this.addRow(p.entry)
        if (this.rows.length > RENDER_MAX) { this.rows.shift()?.destroy(); this.reflowRows() }
        if (pinned) { this.scrollOffset = this.maxScroll() }
        this.applyScroll()
    }

    private clearRows(): void {
        this.rows.forEach(r => r.destroy())
        this.rows = []
        this.placeholder?.destroy(); this.placeholder = undefined
        this.contentH = 0
        this.scrollOffset = 0
    }

    private maxScroll(): number { return Math.max(0, this.contentH - this.vpH) }
    private setScroll(v: number): void { this.scrollOffset = Math.max(0, Math.min(this.maxScroll(), v)); this.applyScroll() }
    private applyScroll(): void { this.list.setPosition(this.vpX, this.vpY - this.scrollOffset) }
    private inViewport(p: Phaser.Input.Pointer): boolean {
        return p.x >= this.vpX && p.x <= this.vpX + this.vpW && p.y >= this.vpY && p.y <= this.vpY + this.vpH
    }
}
